import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, Check, X, Eye, EyeOff, ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

const inputClass =
  'mt-1.5 rounded-xl bg-muted border-border focus:border-vintage focus:ring-1 focus:ring-vintage font-body text-foreground placeholder:text-foreground/40';

function PasswordChecks({ password }) {
  const checks = [
    { label: '8+ characters', ok: password.length >= 8 },
    { label: 'Uppercase (A-Z)', ok: /[A-Z]/.test(password) },
    { label: 'Lowercase (a-z)', ok: /[a-z]/.test(password) },
    { label: 'Number (0-9)', ok: /\d/.test(password) },
    { label: 'Special char (!@#...)', ok: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(password) },
  ];
  if (!password) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="reset-password-strength">
      {checks.map((c, i) => (
        <div key={i} className="flex items-center gap-2">
          {c.ok ? <Check className="h-3 w-3 text-green-400" /> : <X className="h-3 w-3 text-red-400" />}
          <span className={`font-body text-[11px] ${c.ok ? 'text-green-400' : 'text-foreground/40'}`}>{c.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const isValid =
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password) &&
    /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
      toast.success('Password reset. Please sign in.');
      setTimeout(() => navigate('/auth'), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="reset-no-token">
        <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-4" strokeWidth={1.5} />
        <h1 className="font-heading text-2xl text-foreground mb-2">Invalid reset link</h1>
        <p className="font-body text-sm text-foreground/60 mb-6">
          This link is missing its reset token. Please request a new one from the sign-in page.
        </p>
        <Link to="/auth">
          <Button variant="ctaPrimary" className="rounded-full font-body text-sm font-bold px-6">
            Back to sign in
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="reset-password-page" className="max-w-md mx-auto px-4 py-12 sm:py-20">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-full bg-teal/15 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="h-7 w-7 text-teal" />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-light text-foreground tracking-tight mb-2">
          Set a new password
        </h1>
        <p className="font-body text-sm text-foreground/70">Choose a strong password for your account.</p>
      </div>

      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border">
        {done ? (
          <div className="text-center py-6" data-testid="reset-success">
            <Check className="h-10 w-10 text-green-400 mx-auto mb-4" />
            <p className="font-body text-sm text-foreground">Your password has been updated. Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
            <div>
              <Label className="font-body text-sm text-foreground font-medium">New password</Label>
              <div className="relative">
                <Input
                  data-testid="reset-password-input"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-10`}
                  placeholder="Create a strong password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  data-testid="reset-toggle-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground/70"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordChecks password={password} />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground font-medium">Confirm password</Label>
              <Input
                data-testid="reset-confirm-input"
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={inputClass}
                placeholder="Re-enter your password"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="reset-submit"
              variant="ctaPrimary"
              disabled={loading || !isValid}
              className="w-full rounded-full py-6 font-body text-sm font-bold disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
